import React, { useState, useMemo, useCallback, useRef } from 'react';
import '../styles/IdentifyContactModal.css';
import { Input, PredictiveSearch } from '../components';

const CONTACT_FIELDS = [
    { key: 'name', label: 'Name' },
    { key: 'company', label: 'Company' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' }
];

const TYPE_ICONS = {
    string: 'T',
    number: '#',
    boolean: 'B'
};

// Recursive tree node for displaying nested data
function TreeNode({ label, value, depth, onClickValue }) {
    const [expanded, setExpanded] = useState(depth < 1);

    if (value === null || value === undefined || value === '') {
        return null;
    }

    // Array
    if (Array.isArray(value)) {
        const nonEmpty = value.filter(v => v !== null && v !== undefined && v !== '');
        if (nonEmpty.length === 0) return null;

        return (
            <div className="ic-tree-node">
                <div
                    className="ic-tree-row ic-tree-row-parent"
                    onClick={() => setExpanded(prev => !prev)}
                >
                    <span className={`ic-tree-chevron ${expanded ? 'ic-tree-chevron-open' : ''}`}>
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M3 2L7 5L3 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </span>
                    <span className="ic-tree-type-icon ic-tree-type-array label-bold">[ ]</span>
                    <span className="label-bold colour-primary">{label}</span>
                    <span className="label-regular colour-tertiary">{nonEmpty.length} items</span>
                </div>
                {expanded && (
                    <div className="ic-tree-children">
                        {nonEmpty.map((item, i) => (
                            <TreeNode
                                key={i}
                                label={`${label}[${i}]`}
                                value={item}
                                depth={depth + 1}
                                onClickValue={onClickValue}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    // Object
    if (typeof value === 'object') {
        const entries = Object.entries(value).filter(([k, v]) =>
            !k.startsWith('_') && v !== null && v !== undefined && v !== ''
        );
        if (entries.length === 0) return null;

        return (
            <div className="ic-tree-node">
                <div
                    className="ic-tree-row ic-tree-row-parent"
                    onClick={() => setExpanded(prev => !prev)}
                >
                    <span className={`ic-tree-chevron ${expanded ? 'ic-tree-chevron-open' : ''}`}>
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M3 2L7 5L3 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </span>
                    <span className="ic-tree-type-icon ic-tree-type-object label-bold">{ }</span>
                    <span className="label-bold colour-primary">{label}</span>
                </div>
                {expanded && (
                    <div className="ic-tree-children">
                        {entries.map(([k, v]) => (
                            <TreeNode
                                key={k}
                                label={k}
                                value={v}
                                depth={depth + 1}
                                onClickValue={onClickValue}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    // Primitive — clickable leaf
    const typeIcon = TYPE_ICONS[typeof value] || 'T';
    const displayValue = String(value);

    return (
        <div className="ic-tree-node">
            <div
                className="ic-tree-row ic-tree-row-leaf"
                onClick={() => onClickValue(displayValue)}
            >
                <span className="ic-tree-type-icon ic-tree-type-primitive label-bold">{typeIcon}</span>
                <span className="label-bold colour-primary">{label}</span>
                <span className="body-regular colour-secondary">{displayValue}</span>
            </div>
        </div>
    );
}

function IdentifyContactModal({ rawData, values, setValues, contacts = [] }) {
    const [focusedField, setFocusedField] = useState('name');
    const focusedFieldRef = useRef('name');
    const [showSuggestions, setShowSuggestions] = useState(true);

    const parsedData = useMemo(() => {
        if (!rawData) return null;
        try {
            const obj = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
            const filtered = {};
            for (const [k, v] of Object.entries(obj)) {
                if (k.startsWith('_')) continue;
                if (v === null || v === undefined || v === '' || v === false) continue;
                filtered[k] = v;
            }
            return Object.keys(filtered).length > 0 ? filtered : null;
        } catch {
            return null;
        }
    }, [rawData]);

    // Filter contacts based on current input for the focused field
    const filteredContacts = useMemo(() => {
        if (!contacts || contacts.length === 0) return [];
        const query = (values[focusedField] || '').toLowerCase().trim();
        if (!query || query.length < 2) return [];

        return contacts
            .filter(c => {
                const fieldVal = c[focusedField];
                return fieldVal && fieldVal.toLowerCase().includes(query);
            })
            .slice(0, 8)
            .map(c => ({
                label: [c.name, c.company, c.email].filter(Boolean).join(' — '),
                value: c
            }));
    }, [contacts, values, focusedField]);

    const handleFieldFocus = useCallback((fieldKey) => {
        setFocusedField(fieldKey);
        focusedFieldRef.current = fieldKey;
    }, []);

    const handleClickValue = useCallback((val) => {
        const target = focusedFieldRef.current;
        setValues(prev => ({ ...prev, [target]: val }));
    }, [setValues]);

    const handleChange = useCallback((fieldKey, newValue) => {
        setValues(prev => ({ ...prev, [fieldKey]: newValue }));
        setShowSuggestions(true);
    }, [setValues]);

    // When a contact is selected from autocomplete, fill all fields and hide suggestions
    const handleSelectContact = useCallback((item) => {
        const contact = item.value;
        setValues({
            name: contact.name || '',
            company: contact.company || '',
            email: contact.email || '',
            phone: contact.phone || ''
        });
        setShowSuggestions(false);
    }, [setValues]);

    return (
        <div className="identify-contact-modal-body">
            {parsedData && (
                <div className="identify-contact-section">
                    <span className="label-bold colour-primary">Raw data received</span>
                    <span className="label-regular colour-secondary">
                        Click any value to fill the active field, or type manually
                    </span>
                    <div className="identify-contact-raw-data">
                        {Object.entries(parsedData).map(([key, val]) => (
                            <TreeNode
                                key={key}
                                label={key}
                                value={val}
                                depth={0}
                                onClickValue={handleClickValue}
                            />
                        ))}
                    </div>
                </div>
            )}
            <div className="identify-contact-section">
                <span className="label-bold colour-primary">Contact details</span>
                <div className="identify-contact-form">
                    {CONTACT_FIELDS.map(f => (
                        <div
                            key={f.key}
                            className={`ic-field-wrapper ${focusedField === f.key ? 'ic-field-wrapper-active' : ''}`}
                            onFocus={() => handleFieldFocus(f.key)}
                        >
                            <Input
                                label={f.label}
                                labelType="default"
                                info={false}
                                value={values[f.key] || ''}
                                onChange={(e) => handleChange(f.key, e.target.value)}
                                placeholder="Type or click a value above"
                                type="text"
                                leftUnit={null}
                                rightUnit={null}
                                unitSwitch={false}
                                leftUnitSelected={true}
                                unitSwitchFunc={null}
                                disabled={false}
                                multiline={false}
                                error={false}
                                onMouseEnter={null}
                                onMouseLeave={null}
                                reset={false}
                                resetFunc={null}
                                resetPosition={null}
                                focusMultiline={false}
                                prompt={false}
                            />
                            {focusedField === f.key && showSuggestions && filteredContacts.length > 0 && (
                                <div className="ic-predictive-wrapper">
                                    <PredictiveSearch
                                        loading={false}
                                        results={filteredContacts}
                                        selectItem={handleSelectContact}
                                        nested={false}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default IdentifyContactModal;
